<template>
  <div class="flex flex-col h-full justify-between" v-if="!showPromptTemplates">
    <div class="grid gap-2 flex-grow h-96">
      <div class="relative w-full grid gap-2 flex-1 min-h-0">
        <div id="chat-container" class="py-5 overflow-y-auto overflow-x-hidden" ref="chatContainer">
          <div class="whitespace-normal w-full break-words mb-3">
            <div v-for="(chat, index) in chats" :key="index">
              <div class="flex justify-start" v-if="chat.user">
                <div
                  class="bg-white shadow-lg border-solid border-2 border-primary-main py-2 px-4 mt-5 ml-5 rounded-md max-w-md relative">
                  <p class="text-sm">{{ chat.user }}</p>
                </div>
              </div>

              <div class="text-generator-chat-box-step flex justify-end" v-if="chat.botFormatted">
                <div class="bg-white shadow-lg mr-5 mt-5 rounded-md w-11/12 relative">
                  <div class="py-2 px-4">
                    <div class="absolute w-3 h-3 bg-white transform rotate-45 -translate-y-1/2 top-7 -right-1"></div>
                    <div :class="chat.error ? 'text-red' : ''" v-html="chat.botFormatted"></div>
                  </div>
                  <div v-if="chats.length - 1 === index &&
                    !loadingResponse &&
                    !chats[chats.length - 1]?.error
                  " class="p-0 m-0 w-full items-center">
                    <div :class="{
                      'p-2 border-4 border-red-500 rounded-lg':
                        currentStep === 'text-generator-chat-box'
                    }">
                      <button variant="flat" :disabled="!chats[chats.length - 1]?.bot || loadingResponse"
                        :loading="loadingResponse" @click="insertLastSentence"
                        class="!w-full !bg-primary-main !capitalize !text-white font-semibold !py-3 !py-2 !ml-auto pl-7">
                        Insert Text
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="w-full flex justify-center">
                <v-btn v-if="index !== chats.length - 1 && !chat.error" variant="flat" @click="insertSentence(chat.bot)"
                  class="!py-3 !bg-primary-main !m-5 !rounded-md !tracking-normal !capitalize !text-white !h-auto">
                  Insert Text
                </v-btn>
              </div>
              <div class="w-full flex justify-center p-3" v-if="index === chats.length - 1 &&
                chats[index].finishReason === 'length' &&
                !chat.error
              ">
                <v-btn variant="flat" @click="continueResponse()"
                  class="!py-3 !bg-primary-main !m-5 !rounded-md !tracking-normal !capitalize !text-white !h-auto">
                  <template v-slot:prepend>
                    <Icon icon="ant-design:forward-outlined" class="text-lg" />
                  </template>
                  Continue response
                </v-btn>
              </div>
              <div v-if="chat?.cta?.length" class="flex grid gap-0.5 grid-cols-2 ml-6">
                <div :class="['bg-[#e0e1e6] border-[#e0e1e6] shadow-lg mr-5 mt-5 rounded-md relative col-span-2']">
                  <div class="py-2 px-4">
                    <b>Click on one of the options below or use the chat box to ask other questions</b>
                  </div>
                </div>
                <div v-for="(ct, ctIndex) in chat.cta" :key="ctIndex" @click="handleCTAResponse(index, ctIndex)" :class="['bg-white shadow-lg mr-5 mt-5 rounded-md relative cursor-pointer',
                  ct.selected ? 'border-solid border-2 border-primary-main' : '',
                  (ctIndex === chat.cta.length - 1 && chat?.cta?.length % 2 !== 0) ? 'col-span-2 w-full' : 'w-11/12'
                ]">
                  <div :class="`py-2 px-4`">
                    <div v-html="ct.content"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="loadingResponse" class="absolute bottom-0 w-full">
          <!--<div v-if="loadingResponse" class="bg-[#F5F5F5] p-5 pl-7">
            <div class="flex justify-center items-center">
              <div class="w-3 h-3 bg-[#9AD0FF] rounded-full mx-1 animate-bounce-blue-1"></div>
              <div class="w-3 h-3 bg-[#5DB0FF] rounded-full mx-1 animate-bounce-blue-2"></div>
              <div class="w-3 h-3 bg-[#3995FF] rounded-full mx-1 animate-bounce-blue-3"></div>
            </div>
          </div> -->
          <div class="w-full">
            <div class="w-full flex justify-center">
              <v-btn @click="stopChatReply"
                class="!py-3 !bg-white shadow-lg !m-5 !rounded-md !tracking-normal !capitalize !h-auto">
                <template v-slot:prepend>
                  <Icon icon="ant-design:square-outlined" class="text-xs bg-primary-main" />
                </template>
                Stop chat reply
              </v-btn>
            </div>
          </div>
        </div>
        <!-- <button class="">Insert Text</button> -->
      </div>
    </div>
    <div class="w-full z-[10]">
      <div class="text-generator-input-box-step">
        <div class="grid gap-2 mt-3">
          <div class="flex space-x-1 items-center">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <Icon v-bind="props" icon="ant-design:info-circle" class="text-xl cursor-pointer" />
              </template>
              <div class="w-40 whitespace-normal text-center">
                Provide a brief prompt, phrase, or sentence about the topic.
              </div>
            </v-tooltip>
            <span class="font-semibold text-sm 2xl:text-base">Respond Here</span>
          </div>
          <div :class="{
            'p-2 border-4 border-red-500 rounded-lg': currentStep === 'text-generator-input-box'
          }">
            <TlTextArea rows="3" v-model="prompt" />
          </div>
        </div>

        <div class="w-full mt-3 mb-3">
          <div :class="{
            'p-2 border-4 border-red-500 rounded-lg': currentStep === 'text-generator-input-box'
          }">
            <v-btn :disabled="!prompt || loadingResponse" :loading="loadingResponse" @click="generateText()"
              variant="flat"
              class="!py-3 w-full !bg-primary-main !ml-auto !rounded-xl !tracking-normal !capitalize !text-white !h-auto">
              Generate Text
              <template v-slot:append>
                <Icon icon="ant-design:send-outlined" class="text-xl" />
              </template>
            </v-btn>
            <small class="text-center font-bold">Now integrated with GPT-4o!</small>
          </div>
          <!-- <p v-if="loadingResponse">Estimated generation time: <span id="countdownTimer"></span></p> -->
        </div>
      </div>

      <div class="flex justify-center">
        <div class="w-full h-0.5 bg-black/10 shadow-[0_2px_5px_rgba(0,0,0,0.2)] z-[15]"></div>
        <div class="w-full flex justify-center">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <div class="flex items-center px-6 pb-5">
                <Icon v-bind="props" icon="ant-design:info-circle" class="text-xl cursor-pointer" />
                <v-btn @click="showPromptTemplates = true" variant="flat"
                  class="!py-3 !bg-primary-main !ml-2 !rounded-xl !tracking-normal !capitalize !text-white !h-auto ripple-effect">
                  <template v-slot:prepend>
                    <Icon icon="ant-design:copy-outlined" class="text-xl" />
                  </template>
                  Prompt Templates
                </v-btn>

              </div>
            </template>
            <div class="w-40 whitespace-normal text-center">
              A prompt is an instruction that you give to ChatGPT. Check out the thousands of prompts
              that will help you start.
            </div>
          </v-tooltip>
        </div>
        <div class="w-full h-0.5 bg-black/10 shadow-[0_2px_5px_rgba(0,0,0,0.2)] z-[15]"></div>
      </div>
    </div>
  </div>
  <div v-else>
    <AITextGeneratorPromptTemplates @closePromptTemplates="showPromptTemplates = false"
      @insertPrompt="handleInsertPrompt" :promptTemplateFeedbackData="getPromptTemplateFeedbackData"
      :promptTemplateCategoryData="getPromptTemplateCategoryData" />
  </div>
  <v-dialog v-model="showModal" :max-width="'40%'">
    <v-card>
      <v-toolbar dark :color="'primary-main lighten-'" dense flat>
        <v-toolbar-title class="text-body-2 font-weight-bold text-white">

          <span class="">{{ fetchFunction(payloadStore.payload.selectedModule)?.title ?? '' }}</span>

          <v-btn class="" icon="mdi-close" variant="text" @click="showModal = false"></v-btn>
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pa-4 black--text">
        <div class="py-2 px-4">
          <div v-html="initQuestion"></div>
          <div class="pt-4">
            <TlTextArea :noResize="true" rows="5" v-model="prompt" />
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn :disabled="!prompt || loadingResponse" @click="generateText()" :loading="loadingResponse" variant="flat"
          class="!py-3 !bg-primary-main !rounded-xl !tracking-normal !capitalize !text-white !h-auto min-w-[120px]">
          Next
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import TlTextArea from '@/components/global/TlTextArea.vue';
import TlTextField from '@/components/global/TlTextField.vue';
import AITextGeneratorPromptTemplates from '@/components/home/AITextGenerator/AITextGeneratorPromptTemplates.vue';
import { Icon } from '@iconify/vue';
import { Vue, Options } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { usePayloadStore } from '@/stores/payload';
import Message from 'antd-message';
import { v4 as uuidv4 } from 'uuid';
import { promptTemplates } from '@/actions/functions';
import { socket } from "../../../socket";
import { homeTopNavLinks } from '@/utils/homeLinks';
import { formatMarkdown } from '@/utils/markdownFormatter';

@Options({
  components: {
    TlTextArea,
    TlTextField,
    Icon,
    AITextGeneratorPromptTemplates
  }
})
export default class AITextGenerator extends Vue {
  @Prop()
  tour!: any;

  public currentStep = '';
  public prompt = '';
  // public noOfSections = 0;
  // public noOfParagraphPerSection = 0;
  public showPromptTemplates = false;
  public maxLength = 100;
  public loadingResponse = false;
  public payloadStore = usePayloadStore();
  public generationCountdown = 0;
  public timerId: any;
  public sessionId: any;
  public step: any = 0;
  public chats: any = [];
  public promptTemplateFeedbackData: any = [];
  public promptTemplateCategoryData: any = [];
  public showModal: boolean = false;
  public initQuestion: string = '';
  public homeTopNavLinks: any = homeTopNavLinks;

  get promptLength() {
    return this.prompt.length;
  }

  get getPromptTemplateFeedbackData() {
    return this.promptTemplateFeedbackData;
  }

  get getPromptTemplateCategoryData() {
    return this.promptTemplateCategoryData;
  }

  mounted() {
    socket.off();
    this.sessionId = uuidv4();
    this.handleFetchPromptTemplates();
    this.prompt = '';
    this.showModal = true;
    this.generateText();
  }

  @Watch('payloadStore.payload.tour')
  public onTourLoaded() {
    console.log(this.tour?.isActive(), this.payloadStore.payload.tour, 'bbbbb');
    if (this.tour?.isActive()) {
      this.currentStep = this.payloadStore.payload?.tour?.currentStep;
      console.log(this.currentStep);
      if (this.payloadStore.payload?.tour?.loaded) {
        this.prompt = 'Outline the structure and key arguments of the Introduction section of my research paper on the use of nanomaterials in solar panel efficiency';
      }
      if (
        this.payloadStore.payload?.tour?.runAITextGeneratorGenerateText &&
        !this.payloadStore.payload?.loadingBrainResponse &&
        !this.chats?.length &&
        this.prompt
      ) {
        this.generateText();
      }
      if (
        this.payloadStore.payload?.tour?.runAITextGeneratorInsertText &&
        !this.payloadStore.payload?.loadingBrainResponse &&
        this.chats[this.chats.length - 1]?.bot
      ) {
        this.payloadStore.change({ tour: { ...this.payloadStore.payload?.tour, runAITextGeneratorInsertText: false } });
        this.insertLastSentence();
      }
    }
  }

  private handleFetchPromptTemplates() {
    this.promptTemplateFeedbackData = [];
    this.payloadStore.change({ loadingBrainResponse: true });
    promptTemplates()
      .then((res: any) => {
        const promptTemplateCategoryData = [];
        const promptTemplateFeedbackData = [];
        let id = 1;
        for (const key of res.sort((a: any, b: any) => a['position'] - b['position'])) {
          promptTemplateCategoryData.push({
            value: id,
            title: key.category
          });
          promptTemplateFeedbackData.push({
            id,
            payload: key.prompts
          });
          id++;
        }
        this.promptTemplateCategoryData = promptTemplateCategoryData;
        this.promptTemplateFeedbackData = promptTemplateFeedbackData;
        this.payloadStore.change({ loadingBrainResponse: false });
      })
      .catch((err) => {
        this.payloadStore.change({ functionError: true });
        this.payloadStore.change({ loadingBrainResponse: false });
      });
  }

  public stopChatReply() {
    this.chats[this.chats.length - 1].stop = true;
    socket.off();
    this.loadingResponse = false;
    this.payloadStore.change({ loadingBrainResponse: false });
    this.scrollToBottom();
    if (this.tour?.isActive()) {
      this.tour.hide();
      this.payloadStore.change({ tour: { ...this.payloadStore.payload?.tour, currentStep: '' } });
    }
  }

  public generateText() {
    this.chats.push({
      user: this.prompt,
      bot: '',
      botFormatted: '',
      error: false,
      stop: false,
      finish_reason: null
    });
    this.scrollToBottom();
    this.loadingResponse = true;

    this.payloadStore.change({ loadingBrainResponse: true });
    socket.on('content', (data: any) => {
      if (data.sessionId === this.sessionId) {
        this.prompt = '';
        if (data.text) {
          this.scrollToBottom();
          this.chats[this.chats.length - 1].bot = `${this.chats[this.chats.length - 1].bot}${data.text
            }`;
          this.step = data.step;
          this.chats[this.chats.length - 1].botFormatted = formatMarkdown(this.chats[
            this.chats.length - 1
          ].bot.replace(/\n/g, '<br />'));

          if (this.step && Number(this.step) <= 0) {
            this.initQuestion = this.chats[this.chats.length - 1].botFormatted;
          } else {
            this.initQuestion = '';
            this.showModal = false;
          }
        } else {
          if (data.status == 'done') {
            console.log(data, 'done');
            if (data.cta) this.chats[this.chats.length - 1].cta = data.cta;
            socket.off();
            this.loadingResponse = false;
            this.payloadStore.change({ loadingBrainResponse: false });
          }
        }
        if (data.finish_reason) {
          this.chats[this.chats.length - 1].finishReason = data.finish_reason;
        }
        if (data.status == 'error') {
          const error = 'An error occurred while attempting to generate text. Please try again.';
          this.loadingResponse = false;
          this.payloadStore.change({ loadingBrainResponse: false });
          this.chats[this.chats.length - 1].bot = `${error}`;
          this.chats[this.chats.length - 1].botFormatted = `${error}`;
          this.chats[this.chats.length - 1].error = true;
          return Message.error(error);
        }
        this.scrollToBottom();
        // if (this.initQuestion && this.showModal)
        //   this.chats.splice(this.chats.length - 1, 1);
      }
    });
    socket.on('error', (error: any) => {
      console.log(error, 'AI text generator socket error')
    });
    socket.emit("wiseup:text", { prompt: this.prompt, userId: this.payloadStore.payload.user.pk, sessionId: this.sessionId, step: String(typeof this.step === 'undefined' ? '' : this.step), version: 'en', token: this.payloadStore.payload.token }, (res: { data: any; }) => {

    });
  }

  public showTour(step: any, scroll: boolean = false) {
    console.log(step, 'sddsdsds');
    if (scroll) {
      const scrollIntoView = document.getElementById('scroll') as HTMLElement;
      scrollIntoView.scrollIntoView(true);
    }
    setTimeout(() => {
      if (localStorage.getItem('tour-status') !== 'inactive' && this.tour?.isActive()) {
        this.tour.show(step);
        console.log({ tour: { ...this.payloadStore.payload?.tour, currentStep: step } }, 'gggfgg')
        this.payloadStore.change({ tour: { ...this.payloadStore.payload?.tour, currentStep: step } });
      } else {
        this.payloadStore.change({ tour: { ...this.payloadStore.payload?.tour, currentStep: '' } });
      }
    }, 100);
  }

  public continueResponse() {
    this.prompt = 'continue';
    this.generateText();
  }

  public handleCTAResponse(chatIndex: number, ctIndex: number) {
    this.chats[chatIndex].cta.forEach((ctaItem: { selected: boolean; }) => {
      ctaItem.selected = false;
    });

    this.chats[chatIndex].cta[ctIndex].selected = true;
    this.prompt = this.chats[chatIndex].cta[ctIndex]?.response ?? '';
    this.step = this.chats[chatIndex].cta[ctIndex]?.step ?? '';
    this.generateText();
  }

  public fetchFunction(module: string) {
    return homeTopNavLinks.find((h) => h.id === module)
  }

  private scrollToBottom() {
    this.$nextTick(() => {
      const container: any = this.$refs.chatContainer;
      container.scrollTop = container.scrollHeight;
    });
  }

  public insertLastSentence() {
    this.insertSentence(this.chats[this.chats.length - 1]?.bot || '');
  }

  public insertSentence(sentence: string) {
    this.$emit('insertSentence', this.removeHtmlTags(sentence));
    this.showTour('text-editor-box');
    //this.sheet = false;
  }

  public removeHtmlTags(text: string) {
    return text.replace(/<\/?[^>]+(>|$)/g, "");
  }

  public handleInsertPrompt(prompt: string) {
    this.showPromptTemplates = false;
    this.prompt = prompt;
  }
}
</script>
<style>
@keyframes bounceColors-1 {

  0%,
  50% {
    background-color: #9ad0ff;
  }

  25% {
    background-color: #5db0ff;
  }

  75% {
    background-color: #3995ff;
  }
}

@keyframes bounceColors-2 {

  0%,
  50% {
    background-color: #3995ff;
  }

  25% {
    background-color: #9ad0ff;
  }

  75% {
    background-color: #5db0ff;
  }
}

@keyframes bounceColors-3 {

  0%,
  50% {
    background-color: #5db0ff;
  }

  25% {
    background-color: #3995ff;
  }

  75% {
    background-color: #9ad0ff;
  }
}

.animate-bounce-blue-1 {
  animation: bounceColors-1 3s infinite;
}

.animate-bounce-blue-2 {
  animation: bounceColors-2 3s infinite;
}

.animate-bounce-blue-3 {
  animation: bounceColors-3 3s infinite;
}

@keyframes ripple {
  0% {
    box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(24, 144, 255, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(24, 144, 255, 0);
  }
}

.ripple-effect {
  animation: ripple 1.5s infinite;
}

.vertical-line {
  position: absolute;
  width: 2px;
  height: 50px;
  background-color: #1890FF;
  animation: blink 1.5s infinite;
}

@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }
}

.button-container {
  position: relative;
  display: inline-block;
}
</style>
